import React, { useMemo, useCallback, useState, useEffect } from "react"
import { Formik, Form as FormikForm } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  Label,
  Button,
  FormFeedback,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"

import {
  useCreateAccessoriesStock,
  useEditAccessoriesStock,
} from "hooks/useAccessoriesStock"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { calculateTotalPrice } from "utils/accessoryUtils"

const AccessoriesStockAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  // -------------------- Fetch dropdown options --------------------
  const { raw: rawOptions, isLoading: optionLoading } =
    useFilteredVehicleOptions()

  // -------------------- State for created options --------------------
  const [createdAccessories, setCreatedAccessories] = useState([])
  const [createdUnits, setCreatedUnits] = useState([])

  // -------------------- Reset created options when modal closes --------------------
  useEffect(() => {
    if (!modal) {
      setCreatedAccessories([])
      setCreatedUnits([])
    }
  }, [modal])

  // -------------------- Combined options --------------------
  const employeeOptions = useMemo(
    () =>
      (rawOptions?.employees ?? []).map(e => ({
        value: e.id?.toString(),
        label: e.name,
      })),
    [rawOptions?.employees]
  )

  const companyOptions = useMemo(
    () =>
      (rawOptions?.accessories_companies ?? []).map(c => ({
        value: c.id?.toString(),
        label: c.name,
      })),
    [rawOptions?.accessories_companies]
  )

  const accessoryOptions = useMemo(() => {
    const rawAccessories = rawOptions?.accessories_lists ?? []
    const combined = [...rawAccessories, ...createdAccessories]
    return combined.map(a => ({
      value: a.id?.toString(),
      label: a.name,
    }))
  }, [rawOptions?.accessories_lists, createdAccessories])

  const unitOptions = useMemo(() => {
    const rawUnits = rawOptions?.units ?? []
    const combined = [...rawUnits, ...createdUnits]
    return combined.map(u => ({
      value: u.id?.toString(),
      label: u.name,
    }))
  }, [rawOptions?.units, createdUnits])

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccessoriesStock()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccessoriesStock()

  // -------------------- Form Initial Values --------------------
  const initialValues = useMemo(() => {
    const initialItems = editedData?.items?.length
      ? editedData.items.map(item => ({
          accessory_id: item.accessory_id?.toString() || "",
          unit_id: item.unit_id?.toString() || "",
          quantity: item.quantity || "",
          unit_price: item.unit_price || "",
        }))
      : [{ accessory_id: "", unit_id: "", quantity: "", unit_price: "" }]

    return {
      memo_no: editedData?.memo_no || "",
      purchase_date: editedData?.purchase_date || "",
      purchase_by_id: editedData?.purchase_by_id?.toString() || "",
      purchase_from_id: editedData?.purchase_from_id?.toString() || "",
      items: initialItems,
    }
  }, [editedData])

  // -------------------- Validation Schema --------------------
  const validationSchema = Yup.object({
    memo_no: Yup.string().max(255),
    purchase_date: Yup.date().required("Please select purchase date"),
    purchase_by_id: Yup.string().required("Please select an employee"),
    purchase_from_id: Yup.string().required("Please select a company"),
    items: Yup.array()
      .of(
        Yup.object({
          accessory_id: Yup.string().required("Select an accessory"),
          unit_id: Yup.string().required("Select a unit"),
          quantity: Yup.number().required("Enter quantity"),
          unit_price: Yup.number().min(0),
          unit_id: Yup.string().when("accessory_id", (accessory_id, schema) => {
            const idStr = accessory_id?.toString() || ""
            const isNew =
              idStr.startsWith("new_") ||
              !accessoryOptions?.map(u => u?.value)?.includes(idStr)
            return isNew ? schema.required("Select a unit") : schema
          }),
        })
      )
      .min(1, "Add at least one accessory"),
  })

  // -------------------- Handle Option Creation --------------------
  const handleCreateOption = useCallback((fieldName, inputValue) => {
    // Generate a temporary ID (negative number to avoid conflicts with real IDs)
    const tempId = `new_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`

    const newOption = {
      id: tempId,
      name: inputValue,
    }

    if (fieldName === "accessory_id") {
      setCreatedAccessories(prev => [...prev, newOption])
    } else if (fieldName === "unit_id") {
      setCreatedUnits(prev => [...prev, newOption])
    }
  }, [])

  // -------------------- Submit Handler --------------------
  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          items: values.items.map(item => ({
            ...item,
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price),
            // If it's a newly created option (starts with "new_"), send the label instead of the temporary ID
            accessory_id: item.accessory_id?.startsWith("new_")
              ? item.accessory_id.split("_")[1] // Get the label part
              : item.accessory_id,
            unit_id: item.unit_id?.startsWith("new_")
              ? item.unit_id.split("_")[1]
              : item.unit_id,
          })),
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ??
            `Accessories Stock ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save accessories stock.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const buttonText = isEdit ? "Save Changes" : "Add Stock"
  const isSubmitDisabled = editLoading || createLoading

  // -------------------- Render --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Accessories Stock" : "Add Accessories Stock"}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={isEdit}
      >
        {formik => {
          // Helper function to add a new item
          const addItem = () => {
            const currentItems = formik.values.items || []
            formik.setFieldValue("items", [
              ...currentItems,
              { accessory_id: "", unit_id: "", quantity: "", unit_price: "" },
            ])
          }

          // Helper function to remove an item
          const removeItem = index => {
            const currentItems = formik.values.items || []
            if (currentItems.length > 1) {
              const newItems = currentItems.filter((_, i) => i !== index)
              formik.setFieldValue("items", newItems)
            }
          }

          // Calculate total price
          const totalPrice = useMemo(
            () => calculateTotalPrice(formik.values.items),
            [formik.values.items]
          )

          return (
            <FormikForm>
              {/* Memo No */}
              <div className="mb-3">
                <Label htmlFor="memo_no">Memo No</Label>
                <InputField
                  type="text"
                  name="memo_no"
                  id="memo_no"
                  value={formik.values.memo_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.memo_no && !!formik.errors.memo_no}
                />
                {formik.touched.memo_no && formik.errors.memo_no && (
                  <FormFeedback type="invalid">
                    {formik.errors.memo_no}
                  </FormFeedback>
                )}
              </div>

              {/* Purchase Date */}
              <div className="mb-3">
                <Label htmlFor="purchase_date">
                  Purchase Date <span className="text-danger">*</span>
                </Label>
                <InputField
                  type="datetime-local"
                  name="purchase_date"
                  id="purchase_date"
                  value={formik.values.purchase_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.purchase_date &&
                    !!formik.errors.purchase_date
                  }
                />
                {formik.touched.purchase_date &&
                  formik.errors.purchase_date && (
                    <FormFeedback type="invalid">
                      {formik.errors.purchase_date}
                    </FormFeedback>
                  )}
              </div>

              {/* Purchased By */}
              <FormikCreatableSelect
                name="purchase_by_id"
                label="Purchased By"
                formik={formik}
                options={employeeOptions}
                placeholder="Select employee"
                loading={optionLoading}
                required
              />

              {/* Purchased From */}
              <FormikCreatableSelect
                name="purchase_from_id"
                label="Purchased From"
                formik={formik}
                options={companyOptions}
                placeholder="Select company"
                loading={optionLoading}
                required
              />

              {/* Dynamic Accessories */}
              {(formik.values.items || []).map((item, index) => (
                <Card key={index} className="mb-3 p-3 border">
                  <CardBody>
                    <h6>Accessory #{index + 1}</h6>

                    <FormikCreatableSelect
                      name={`items.${index}.accessory_id`}
                      label="Accessory"
                      formik={formik}
                      options={accessoryOptions}
                      placeholder="Select accessory"
                      loading={optionLoading}
                      required
                      onCreateOption={inputValue =>
                        handleCreateOption("accessory_id", inputValue)
                      }
                    />
                    {!accessoryOptions
                      ?.map(u => u?.value)
                      ?.includes(
                        formik?.values?.items[index]?.accessory_id
                      ) && (
                      <FormikCreatableSelect
                        name={`items.${index}.unit_id`}
                        label="Unit"
                        formik={formik}
                        options={unitOptions}
                        placeholder="Select unit"
                        loading={optionLoading}
                        required
                        onCreateOption={inputValue =>
                          handleCreateOption("unit_id", inputValue)
                        }
                      />
                    )}

                    <Row className="mt-2">
                      <Col md={6}>
                        <Label>
                          Quantity <span className="text-danger">*</span>
                        </Label>
                        <InputField
                          type="number"
                          name={`items.${index}.quantity`}
                          value={item.quantity}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.items?.[index]?.quantity &&
                          formik.errors.items?.[index]?.quantity && (
                            <FormFeedback type="invalid" className="d-block">
                              {formik.errors.items[index].quantity}
                            </FormFeedback>
                          )}
                      </Col>
                      <Col md={6}>
                        <Label>Unit Price</Label>
                        <InputField
                          type="number"
                          name={`items.${index}.unit_price`}
                          value={item.unit_price}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          step="0.01"
                          min="0"
                        />
                        {formik.touched.items?.[index]?.unit_price &&
                          formik.errors.items?.[index]?.unit_price && (
                            <FormFeedback type="invalid" className="d-block">
                              {formik.errors.items[index].unit_price}
                            </FormFeedback>
                          )}
                      </Col>
                    </Row>

                    <Button
                      color="danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => removeItem(index)}
                      disabled={(formik.values.items || []).length === 1}
                      type="button"
                    >
                      Remove
                    </Button>
                  </CardBody>
                </Card>
              ))}

              <Button
                color="primary"
                type="button"
                onClick={addItem}
                className="mb-3"
              >
                Add Another Accessory
              </Button>

              {/* Display Total Price */}
              <div className="mt-3">
                <strong>Total Price: {totalPrice}</strong>
              </div>

              {/* Display items array error if any */}
              {formik.errors.items &&
                typeof formik.errors.items === "string" && (
                  <div className="text-danger mb-3">{formik.errors.items}</div>
                )}

              {/* Submit */}
              <div className="text-end mt-3">
                <Button
                  color="success"
                  type="submit"
                  disabled={isSubmitDisabled}
                >
                  {buttonText}
                </Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>
    </ThemeModal>
  )
}

export default AccessoriesStockAddEdit
